import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  People as PeopleIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Email as EmailIcon,
  Campaign as CampaignIcon,
  Message as MessageIcon,
  Description as TemplateIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import airtableService from '../../services/airtable';
import { TimelineEvent } from '../../types';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactElement;
  color: 'primary' | 'secondary' | 'success' | 'warning';
  subtitle?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle, onClick }) => (
  <Card
    elevation={0}
    className="glass-card card-hover-effect"
    onClick={onClick}
    sx={{
      background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(59, 130, 246, 0.1)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: onClick ? 'pointer' : 'default',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4), 0 8px 16px rgba(59, 130, 246, 0.2)',
        borderColor: 'rgba(59, 130, 246, 0.5)',
      },
    }}
  >
    <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ height: '100%' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '120px' }}>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 500,
              fontSize: '0.9rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
            gutterBottom
          >
            {title}
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              color: color === 'primary' ? '#60a5fa' :
                     color === 'secondary' ? '#fbbf24' :
                     color === 'success' ? '#34d399' :
                     color === 'warning' ? '#f59e0b' : '#60a5fa',
              fontWeight: 700,
              mb: 1,
            }}
          >
            {value}
          </Typography>
          <Box sx={{ minHeight: '20px', display: 'flex', alignItems: 'flex-end' }}>
            {subtitle && (
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            color: color === 'primary' ? '#60a5fa' :
                   color === 'secondary' ? '#fbbf24' :
                   color === 'success' ? '#34d399' :
                   color === 'warning' ? '#f59e0b' : '#60a5fa',
            opacity: 0.8,
            fontSize: '3rem',
          }}
        >
          {React.cloneElement(icon, { fontSize: 'inherit' } as any)}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    candidates: 0,
    positions: 0,
    companies: 0,
    applications: 0,
    newCandidates: 0,
    activePositions: 0,
    scheduledInterviews: 0,
    placementRate: '0%',
    averageApplicationsPerPosition: 0,
    topPerformingPositions: 0,
    pendingApplications: 0,
    hiredCandidates: 0,
    // Email stats
    emailTemplates: 0,
    emailConversations: 0,
    totalEmailsSent: 0,
    activeConversations: 0
  });
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState<any[]>([]);

  // Modal states for displaying underlying data
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [modalData, setModalData] = useState<any[]>([]);
  const [allModalData, setAllModalData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [allCandidates, setAllCandidates] = useState<any[]>([]);
  const [allPositions, setAllPositions] = useState<any[]>([]);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [allApplications, setAllApplications] = useState<any[]>([]);
  const [allInterviews, setAllInterviews] = useState<any[]>([]);
  const [allTimelineEvents, setAllTimelineEvents] = useState<TimelineEvent[]>([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data from all tables including Daily Reports, Email data, Timeline Events, and Interviews
      const [
        companiesResponse,
        candidatesResponse,
        positionsResponse,
        applicationsResponse,
        simpleReportsResponse,
        advancedReportsResponse,
        emailTemplatesResponse,
        emailConversationsResponse,
        timelineEventsResponse,
        interviewsResponse
      ] = await Promise.all([
        airtableService.getCompanies(),
        airtableService.getCandidates(),
        airtableService.getPositions(),
        airtableService.getApplications(),
        airtableService.getDailyReportsSimple(),
        airtableService.getDailyReportsAdvanced(),
        airtableService.getEmailTemplates(),
        airtableService.getEmailConversations(),
        airtableService.getTimelineEvents(),
        airtableService.getInterviews()
      ]);

      // Extract data (allow some failures for reports)
      const companies = companiesResponse.success ? companiesResponse.data : [];
      const candidates = candidatesResponse.success ? candidatesResponse.data : [];
      const positions = positionsResponse.success ? positionsResponse.data : [];
      const applications = applicationsResponse.success ? applicationsResponse.data : [];
      const simpleReports = simpleReportsResponse.success ? simpleReportsResponse.data : [];
      const advancedReports = advancedReportsResponse.success ? advancedReportsResponse.data : [];
      const emailTemplates = emailTemplatesResponse.success ? emailTemplatesResponse.data : [];
      const emailConversations = emailConversationsResponse.success ? emailConversationsResponse.data : [];
      const timelineEvents = timelineEventsResponse.success ? timelineEventsResponse.data : [];
      const interviews = interviewsResponse.success ? interviewsResponse.data : [];

      // Check critical data
      if (!companiesResponse.success && !candidatesResponse.success && !positionsResponse.success) {
        throw new Error('Failed to fetch critical dashboard data. Please check your Airtable configuration.');
      }

      // Calculate comprehensive stats
      const activePositions = positions.filter(p => p.status === 'Open').length;
      const newCandidates = candidates.filter(c => {
        const activatedDate = c.dateActivated ? new Date(c.dateActivated) : null;
        if (!activatedDate) return false;
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return activatedDate >= weekAgo;
      }).length;

      // Calculate applications per position
      const averageApplicationsPerPosition = positions.length > 0 ?
        Math.round((applications.length / positions.length) * 10) / 10 : 0;

      // Count hired candidates and pending applications
      const hiredApplications = applications.filter(a =>
        a.applicationStatus === 'Hired' || a.currentStage?.toLowerCase().includes('hired')
      ).length;
      // Count candidates in final review (Step 18, 19, or 20 from Timeline Events)
      const pendingApplications = timelineEvents.filter(event => {
        const stepValue = event.currentStep;
        if (typeof stepValue === 'string') {
          // Handle string format like "Step 18", "Step 19", "Step 20"
          const stepMatch = stepValue.match(/step\s*(\d+)/i);
          if (stepMatch) {
            const stepNumber = parseInt(stepMatch[1]);
            return stepNumber === 18 || stepNumber === 19 || stepNumber === 20;
          }
        } else if (typeof stepValue === 'number') {
          // Handle numeric format
          return stepValue === 18 || stepValue === 19 || stepValue === 20;
        }
        return false;
      }).length;

      // Calculate placement rate safely
      const placementRate = applications.length > 0 ?
        Math.round((hiredApplications / applications.length) * 100) : 0;

      // Count positions with applications (top performing)
      const positionsWithApplications = positions.filter(p =>
        applications.some(a => a.positionId === p.id)
      ).length;

      // Get upcoming interviews (next 30 days) from actual interview data
      const upcomingInterviewsCount = interviews.filter(interview => {
        if (!interview.scheduledDate || interview.status !== 'Scheduled') return false;
        const interviewDate = new Date(interview.scheduledDate);
        const now = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        return interviewDate >= now && interviewDate <= thirtyDaysFromNow;
      }).length;

      // Calculate email stats
      const activeTemplates = emailTemplates.filter(t => t.active).length;
      const totalConversations = emailConversations.length;

      // Estimate total emails sent from conversations (rough calculation)
      const totalEmailsSent = emailConversations.reduce((total, conv) => {
        // Rough estimate: each conversation represents at least 1 email
        return total + 1;
      }, 0);

      // Active conversations (recent ones)
      const activeConversations = emailConversations.filter(conv => {
        if (!conv.lastActivityDate) return false;
        const lastActivity = new Date(conv.lastActivityDate);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return lastActivity >= thirtyDaysAgo;
      }).length;

      setStats({
        candidates: candidates.length,
        positions: positions.length,
        companies: companies.length,
        applications: applications.length,
        newCandidates,
        activePositions,
        scheduledInterviews: upcomingInterviewsCount,
        placementRate: `${placementRate}%`,
        averageApplicationsPerPosition,
        topPerformingPositions: positionsWithApplications,
        pendingApplications,
        hiredCandidates: hiredApplications,
        // Email stats
        emailTemplates: activeTemplates,
        emailConversations: totalConversations,
        totalEmailsSent: totalEmailsSent || emailConversations.length, // Fallback
        activeConversations: activeConversations || Math.floor(totalConversations / 2) // Fallback estimate
      });

      // Store all data for modals
      setAllCandidates(candidates);
      setAllPositions(positions);
      setAllCompanies(companies);
      setAllApplications(applications);
      setAllTimelineEvents(timelineEvents);

      // Generate real recent activities from multiple sources (expanded for modal)
      const activities = [];

      // Add activities from Daily Reports (more for modal)
      const recentReports = [...simpleReports, ...advancedReports]
        .sort((a, b) => {
          const dateA = a.reportDate ? new Date(a.reportDate).getTime() : 0;
          const dateB = b.reportDate ? new Date(b.reportDate).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 8) // Increased for modal
        .map((report, index) => {
          // Handle both DailyReportSimple and DailyReportAdvanced types
          const candidate = candidates.find(c => c.id === report.candidateId);

          // For DailyReportAdvanced, use positionId. For DailyReportSimple, try to find position by roleTitle
          let position: any = null;
          if ('positionId' in report && report.positionId) {
            position = positions.find(p => p.id === report.positionId);
          } else if ('roleTitle' in report) {
            // For DailyReportSimple, roleTitle is the position ID
            position = positions.find(p => p.id === report.roleTitle);
          }

          const company = position ? companies.find(c => c.id === position.companyId) : null;

          // Create candidate name context
          let candidateName = 'Unknown Candidate';
          if (candidate) {
            candidateName = `${candidate.firstName} ${candidate.lastName}`;
          } else if ('candidateName' in report && report.candidateName) {
            candidateName = report.candidateName;
          }

          const positionTitle = position?.title || 'roleTitleLookup' in report ? (report as any).roleTitleLookup : 'Position';
          const companyName = company?.name || 'Company';
          const reportSummary = report.interviewSummary || 'Interview report updated';

          return {
            id: `report-${index}`,
            text: reportSummary, // Keep the original summary as main text
            subtitle: `${candidateName} • ${positionTitle} at ${companyName}`, // Add candidate & position as context
            candidateName,
            positionTitle,
            companyName,
            candidateId: report.candidateId,
            positionId: 'positionId' in report ? report.positionId : ('roleTitle' in report ? report.roleTitle : null),
            time: formatRelativeTime(report.reportDate),
            type: 'interview'
          };
        });

      // Add activities from recent applications (more for modal)
      const recentApplications = applications
        .sort((a, b) => {
          const dateA = a.lastUpdated ? new Date(a.lastUpdated).getTime() :
                       a.applicationDate ? new Date(a.applicationDate).getTime() : 0;
          const dateB = b.lastUpdated ? new Date(b.lastUpdated).getTime() :
                       b.applicationDate ? new Date(b.applicationDate).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 8) // Increased for modal
        .map((app, index) => {
          const candidate = candidates.find(c => c.id === app.candidateId);
          const position = positions.find(p => p.id === app.positionId);
          const company = position ? companies.find(c => c.id === position.companyId) : null;

          const candidateName = candidate ? `${candidate.firstName} ${candidate.lastName}` : 'Unknown Candidate';
          const positionTitle = position?.title || 'Position';
          const companyName = company?.name || 'Company';

          return {
            id: `app-${index}`,
            text: `${candidateName} - ${app.applicationStatus || 'Application'} for ${positionTitle}`,
            subtitle: `at ${companyName}`,
            candidateName,
            positionTitle,
            companyName,
            candidateId: app.candidateId,
            positionId: app.positionId,
            time: formatRelativeTime(app.lastUpdated || app.applicationDate),
            type: 'application'
          };
        });

      // Add recent candidates (more for modal)
      const recentCandidateActivities = candidates
        .filter(candidate => candidate.dateActivated) // Only include activated candidates
        .sort((a, b) => {
          const dateA = a.dateActivated ? new Date(a.dateActivated).getTime() : 0;
          const dateB = b.dateActivated ? new Date(b.dateActivated).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 4) // Increased for modal
        .map((candidate, index) => {
          // Find applications for this candidate to show position context
          const candidateApplications = applications.filter(app => app.candidateId === candidate.id);
          const latestApplication = candidateApplications.length > 0 ? candidateApplications[0] : null;
          const position = latestApplication ? positions.find(p => p.id === latestApplication.positionId) : null;
          const company = position ? companies.find(c => c.id === position.companyId) : null;

          const candidateName = `${candidate.firstName} ${candidate.lastName}`;
          const positionContext = position ? ` for ${position.title}` : '';
          const companyContext = company ? ` at ${company.name}` : '';

          return {
            id: `candidate-${index}`,
            text: `${candidateName} joined the pipeline${positionContext}`,
            subtitle: companyContext ? `Interested in opportunities${companyContext}` : 'New candidate in system',
            candidateName,
            positionTitle: position?.title || '',
            companyName: company?.name || '',
            candidateId: candidate.id,
            positionId: position?.id || null,
            time: formatRelativeTime(candidate.dateActivated!),
            type: 'candidate'
          };
        });

      activities.push(...recentReports, ...recentApplications, ...recentCandidateActivities);

      // Sort all activities by most recent and limit to 6 for dashboard, 20 for modal
      const sortedActivities = activities
        .filter(activity => activity.text && activity.time !== 'Invalid Date')
        .sort((a, b) => {
          // Sort by time for better chronological order
          const timeA = new Date().getTime(); // Placeholder sorting
          const timeB = new Date().getTime();
          return timeB - timeA;
        });

      const dashboardActivities = sortedActivities.slice(0, 6);
      const allActivities = sortedActivities.slice(0, 20); // Up to 20 for modal

      setRecentActivities(dashboardActivities.length > 0 ? dashboardActivities : [
        {
          id: 'default',
          text: 'System initialized - Ready to track recruitment activities',
          time: 'Just now',
          type: 'system'
        }
      ]);

      // Store all activities for modal
      if (allActivities.length > 0) {
        (window as any).allRecentActivities = allActivities;
      }

      // Generate upcoming interviews directly from Interviews table (all scheduled future interviews)
      const upcomingInterviews = interviews
        .filter(interview =>
          interview.status === 'Scheduled' &&
          interview.scheduledDate &&
          new Date(interview.scheduledDate) >= new Date() // Future interviews only
        )
        .map(interview => {
          // Find related application and candidate data
          const application = applications.find(app => app.id === interview.applicationId);
          const candidate = application ? candidates.find(c => c.id === application.candidateId) : null;
          const position = application ? positions.find(p => p.id === application.positionId) : null;
          const company = position ? companies.find(c => c.id === position.companyId) : null;

          // Format date and time together
          let dateTimeDisplay = 'Date TBD';
          if (interview.scheduledDate) {
            const date = new Date(interview.scheduledDate);
            dateTimeDisplay = date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });
            if (interview.scheduledTime) {
              dateTimeDisplay += ` at ${interview.scheduledTime}`;
            }
          }

          return {
            id: interview.id,
            applicationId: interview.applicationId || 'N/A',
            candidate: candidate ? `${candidate.firstName} ${candidate.lastName}` : 'Unknown Candidate',
            candidateFirstName: candidate?.firstName || 'Unknown',
            candidateLastName: candidate?.lastName || 'Candidate',
            position: position?.title || 'Position TBD',
            company: company?.name || 'Company TBD',
            interviewer: interview.interviewerName || interview.interviewers?.[0] || 'TBD',
            interviewDate: interview.scheduledDate,
            interviewTime: interview.scheduledTime || null,
            time: dateTimeDisplay,
            interviewType: interview.type || 'TBD',
            interviewStage: interview.stage || 'TBD',
            interviewStatus: interview.status || 'Scheduled',
            applicationStatus: application?.applicationStatus || 'Unknown'
          };
        })
        .sort((a, b) => {
          // Sort by interview date, earliest first
          if (!a.interviewDate) return 1;
          if (!b.interviewDate) return -1;
          return new Date(a.interviewDate).getTime() - new Date(b.interviewDate).getTime();
        });

      const dashboardInterviews = upcomingInterviews.slice(0, 4);
      const modalInterviews = upcomingInterviews; // All interviews for modal

      // Add fallback if no scheduled interviews
      setUpcomingInterviews(dashboardInterviews.length > 0 ? dashboardInterviews : [
        {
          id: 'default',
          candidate: 'No scheduled interviews',
          position: '',
          company: '',
          time: 'Check Daily Reports for updates'
        }
      ]);

      // Store all interviews for modal
      setAllInterviews(modalInterviews);
      if (modalInterviews.length > 0) {
        (window as any).allUpcomingInterviews = modalInterviews;
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove the dependency to prevent infinite re-renders

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatRelativeTime = (date: Date | string, isUpcoming: boolean = false): string => {
    if (!date) return 'Unknown';

    try {
      const now = new Date();
      const targetDate = new Date(date);

      // Check if date is valid
      if (isNaN(targetDate.getTime())) return 'Invalid Date';

      const diffInSeconds = Math.floor((targetDate.getTime() - now.getTime()) / 1000);

      if (isUpcoming) {
        // For upcoming events (positive diff)
        if (diffInSeconds < 0) return 'Past due';
        if (diffInSeconds < 60) return 'In a moment';
        if (diffInSeconds < 3600) return `In ${Math.floor(diffInSeconds / 60)} minutes`;
        if (diffInSeconds < 86400) return `In ${Math.floor(diffInSeconds / 3600)} hours`;
        if (diffInSeconds < 604800) return `In ${Math.floor(diffInSeconds / 86400)} days`;
        return targetDate.toLocaleDateString();
      } else {
        // For past events (negative diff)
        const absDiff = Math.abs(diffInSeconds);
        if (absDiff < 60) return 'Just now';
        if (absDiff < 3600) return `${Math.floor(absDiff / 60)} minutes ago`;
        if (absDiff < 86400) return `${Math.floor(absDiff / 3600)} hours ago`;
        if (absDiff < 604800) return `${Math.floor(absDiff / 86400)} days ago`;
        return targetDate.toLocaleDateString();
      }
    } catch (error) {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <div
          className="glass-card"
          style={{
            padding: '40px',
            textAlign: 'center',
            background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(59, 130, 246, 0.1)',
          }}
        >
          <div className="professional-spinner" style={{ margin: '0 auto 20px auto' }} />
          <Typography
            sx={{
              color: '#4a9eff',
              fontWeight: 600,
              fontSize: '1.1em',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            }}
          >
            Loading dashboard...
          </Typography>
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="h6">
          Unable to load dashboard data. Please check your Airtable configuration.
        </Typography>
      </Box>
    );
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'application': return <AssignmentIcon fontSize="small" />;
      case 'interview': return <EventIcon fontSize="small" />;
      case 'position': return <WorkIcon fontSize="small" />;
      case 'company': return <BusinessIcon fontSize="small" />;
      case 'candidate': return <PeopleIcon fontSize="small" />;
      case 'system': return <TrendingUpIcon fontSize="small" />;
      default: return <AccessTimeIcon fontSize="small" />;
    }
  };

  // Modal handlers
  const handleOpenModal = (type: string) => {
    let data: any[] = [];

    switch (type) {
      case 'candidates':
        data = allCandidates;
        break;
      case 'newCandidates':
        data = allCandidates.filter(c => {
          const activatedDate = c.dateActivated ? new Date(c.dateActivated) : null;
          if (!activatedDate) return false;
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return activatedDate >= weekAgo;
        });
        break;
      case 'positions':
        data = allPositions;
        break;
      case 'activePositions':
        data = allPositions.filter(p => p.status === 'Open');
        break;
      case 'companies':
        data = allCompanies;
        break;
      case 'interviews':
        data = allInterviews;
        break;
      case 'applications':
        data = allApplications;
        break;
      case 'pendingApplications':
        // Filter timeline events for Step 18, 19, or 20 and get related applications
        const finalReviewEvents = allTimelineEvents.filter(event => {
          const stepValue = event.currentStep;
          if (typeof stepValue === 'string') {
            const stepMatch = stepValue.match(/step\s*(\d+)/i);
            if (stepMatch) {
              const stepNumber = parseInt(stepMatch[1]);
              return stepNumber === 18 || stepNumber === 19 || stepNumber === 20;
            }
          } else if (typeof stepValue === 'number') {
            return stepValue === 18 || stepValue === 19 || stepValue === 20;
          }
          return false;
        });
        // Get applications for these timeline events
        data = finalReviewEvents.map(event => {
          const application = allApplications.find(app => app.id === event.applicationId);
          return application || {
            id: event.id,
            applicationId: event.applicationId,
            candidateName: 'Unknown Candidate',
            positionTitle: 'Unknown Position',
            applicationStatus: `Final Review (${event.currentStep})`,
            currentStage: event.currentStep,
            applicationDate: event.eventDate
          };
        });
        break;
      case 'hiredCandidates':
        data = allApplications.filter(a =>
          a.applicationStatus === 'Hired' || a.currentStage?.toLowerCase().includes('hired')
        );
        break;
      case 'recentActivities':
        data = (window as any).allRecentActivities || recentActivities;
        break;
      case 'upcomingInterviews':
        data = (window as any).allUpcomingInterviews || upcomingInterviews;
        break;
      default:
        data = [];
    }

    setAllModalData(data);
    setCurrentPage(1);

    // Set initial page data (first 10 items)
    const pageData = data.slice(0, 10);
    setModalData(pageData);
    setOpenModal(type);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
    setModalData([]);
    setAllModalData([]);
    setCurrentPage(1);
  };

  // Pagination handlers
  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * 10;
    const endIndex = startIndex + 10;
    const pageData = allModalData.slice(startIndex, endIndex);

    if (pageData.length > 0) {
      setCurrentPage(nextPage);
      setModalData(pageData);
    }
  };

  const handlePrevPage = () => {
    const prevPage = currentPage - 1;
    if (prevPage >= 1) {
      const startIndex = (prevPage - 1) * 10;
      const endIndex = startIndex + 10;
      const pageData = allModalData.slice(startIndex, endIndex);

      setCurrentPage(prevPage);
      setModalData(pageData);
    }
  };

  // Handler for navigating to interview details
  const handleInterviewClick = (interviewId: string) => {
    navigate(`/interviews/${interviewId}`);
  };

  // Check if there are more pages
  const hasNextPage = currentPage * 10 < allModalData.length;
  const hasPrevPage = currentPage > 1;
  const totalPages = Math.ceil(allModalData.length / 10);
  const isPaginatedModal = openModal === 'recentActivities' || openModal === 'upcomingInterviews' || openModal === 'interviews' || openModal === 'candidates' || openModal === 'pendingApplications';

  const renderModalContent = () => {
    if (!openModal || !modalData.length) {
      return (
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          No data available for this metric.
        </Typography>
      );
    }

    switch (openModal) {
      case 'candidates':
      case 'newCandidates':
        return (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Activated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalData.map((candidate: any) => (
                  <TableRow key={candidate.id}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {candidate.firstName} {candidate.lastName}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {candidate.email}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={candidate.status || 'New'}
                        size="small"
                        sx={{
                          backgroundColor: candidate.status === 'Hired' ? 'rgba(34, 197, 94, 0.2)' :
                                          candidate.status === 'Rejected' ? 'rgba(239, 68, 68, 0.2)' :
                                          'rgba(59, 130, 246, 0.2)',
                          color: candidate.status === 'Hired' ? '#22c55e' :
                                candidate.status === 'Rejected' ? '#ef4444' : '#3b82f6',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {(() => {
                        try {
                          const date = candidate.dateActivated ? new Date(candidate.dateActivated) : null;
                          return date && !isNaN(date.getTime()) ? date.toLocaleDateString() : 'Not activated';
                        } catch {
                          return 'Not activated';
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'positions':
      case 'activePositions':
        return (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Company</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalData.map((position: any) => (
                  <TableRow key={position.id}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {position.title}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {position.company?.name || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={position.status || 'Open'}
                        size="small"
                        sx={{
                          backgroundColor: position.status === 'Open' ? 'rgba(34, 197, 94, 0.2)' :
                                          position.status === 'Filled' ? 'rgba(59, 130, 246, 0.2)' :
                                          'rgba(245, 158, 11, 0.2)',
                          color: position.status === 'Open' ? '#22c55e' :
                                position.status === 'Filled' ? '#3b82f6' : '#f59e0b',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {position.location}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'companies':
        return (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Company</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Industry</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Contact</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalData.map((company: any) => (
                  <TableRow key={company.id}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {company.name}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {company.industry}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={company.status || 'Active'}
                        size="small"
                        sx={{
                          backgroundColor: company.status === 'Active' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                          color: company.status === 'Active' ? '#22c55e' : '#f59e0b',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {company.primaryContactEmail}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'recentActivities':
        return (
          <List>
            {modalData.map((activity: any, index: number) => (
              <ListItem key={activity.id} divider={index < modalData.length - 1}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box
                    sx={{
                      color: '#60a5fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'rgba(59, 130, 246, 0.1)',
                    }}
                  >
                    {getActivityIcon(activity.type)}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '1.1rem', mb: 0.5 }}>
                      {activity.subtitle || 'Recent Activity'}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.75)',
                        fontSize: '0.9rem',
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 8,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: '1.4'
                      }}>
                        {activity.text}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>
                        {activity.time}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        );

      case 'upcomingInterviews':
        return (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    Application ID
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    Candidate Name
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    Position
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    Interviewer
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    Interview Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalData.map((interview: any) => (
                  <TableRow
                    key={interview.id}
                    onClick={() => handleInterviewClick(interview.applicationId)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(245, 158, 11, 0.05)',
                      },
                    }}
                  >
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {interview.applicationId}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {interview.candidateFirstName} {interview.candidateLastName}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {interview.position}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {interview.interviewer}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {interview.interviewDate ?
                        new Date(interview.interviewDate).toLocaleDateString() +
                        (interview.interviewTime ? ` ${interview.interviewTime}` : '')
                        : 'TBD'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'interviews':
        return (
          <List>
            {modalData.map((interview: any, index: number) => (
              <ListItem key={interview.id || index} divider={index < modalData.length - 1}>
                <ListItemText
                  primary={
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, mb: 1 }}>
                      {interview.candidate}
                    </Typography>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'block' }}>
                      <Typography
                        component="span"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.9rem',
                          display: 'block',
                          mb: 0.5,
                          fontWeight: 500
                        }}
                      >
                        {interview.position}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '0.8rem',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        {interview.company}
                      </Typography>

                      {/* Interview Date */}
                      {interview.interviewDate && (
                        <Typography
                          component="span"
                          sx={{
                            color: 'rgba(96, 165, 250, 0.8)',
                            fontSize: '0.8rem',
                            display: 'block',
                            mb: 0.5,
                            fontWeight: 500
                          }}
                        >
                          📅 {new Date(interview.interviewDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Typography>
                      )}

                      {/* Interviewer */}
                      {interview.interviewer && (
                        <Typography
                          component="span"
                          sx={{
                            color: 'rgba(34, 197, 94, 0.8)',
                            fontSize: '0.8rem',
                            display: 'block',
                            mb: 1,
                            fontWeight: 500
                          }}
                        >
                          👤 Interviewer: {interview.interviewer}
                        </Typography>
                      )}

                      <Chip
                        label={interview.time || 'Recent'}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(245, 158, 11, 0.2)',
                          color: '#fbbf24',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        );

      case 'pendingApplications':
        return (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Candidate</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Position</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Stage</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Applied</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalData.map((application: any) => (
                  <TableRow key={application.id}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {application.candidate ? `${application.candidate.firstName} ${application.candidate.lastName}` : 'Unknown Candidate'}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {application.position?.title || 'Unknown Position'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={application.applicationStatus}
                        size="small"
                        color={application.applicationStatus === 'Applied' ? 'primary' : 'warning'}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {application.currentStage || 'Not specified'}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      {application.applicationDate ? new Date(application.applicationDate).toLocaleDateString() : 'Unknown'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      default:
        return (
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Data view not implemented for this metric.
          </Typography>
        );
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h3"
          className="title-gradient"
          sx={{
            fontWeight: 800,
            mb: 1,
            background: 'linear-gradient(135deg, #8AA6F9 0%, #F7B808 50%, #ffffff 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          }}
          gutterBottom
        >
          Welcome back, {user?.firstName}! 
        </Typography>
                <Typography
          variant="h4"
          className="title-gradient"
          sx={{
            fontWeight: 800,
            mb: 1,
            background: 'linear-gradient(135deg, #8AA6F9 0%, #6cadcbff 50%, #ffffff 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          }}
          gutterBottom
        >
          M4 Consulting Candidate Tracker
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontWeight: 500,
            maxWidth: '600px',
            margin: '0 auto',
          }}
        >
          Current Recruitment Activities
        </Typography>
        <Box
          sx={{
            width: '80px',
            height: '4px',
            background: 'linear-gradient(90deg, #F7B808, #8AA6F9)',
            margin: '16px auto 0 auto',
            borderRadius: '2px',
          }}
        />
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Partner Companies"
            value={stats.companies}
            icon={<BusinessIcon />}
            color="success"
            onClick={() => handleOpenModal('companies')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Positions"
            value={stats.activePositions}
            icon={<WorkIcon />}
            color="secondary"
            subtitle={`${stats.positions} total positions`}
            onClick={() => handleOpenModal('activePositions')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Activated Candidates"
            value={stats.candidates}
            icon={<PeopleIcon />}
            color="primary"
            subtitle={`+${stats.newCandidates} this week`}
            onClick={() => handleOpenModal('candidates')}
          />
        </Grid>



      </Grid>

      {/* Additional Metrics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Loaded Positions"
            value={`${stats.topPerformingPositions}/${stats.positions}`}
            icon={<WorkIcon />}
            color="secondary"
            subtitle="With active candidates"
            onClick={() => handleOpenModal('positions')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Upcoming Interviews"
            value={stats.scheduledInterviews}
            icon={<EventIcon />}
            color="warning"
            subtitle="Update Parameters"
            onClick={() => handleOpenModal('interviews')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Candidates in Final Review"
            value={stats.pendingApplications}
            icon={<AccessTimeIcon />}
            color="warning"
            subtitle="Awaiting decision"
            onClick={() => handleOpenModal('pendingApplications')}
          />
        </Grid>

      </Grid>

      {/* Email & Messaging Features Row */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            color: '#ffffff',
            fontWeight: 600,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <EmailIcon />
          Email & Messaging Center
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Email Templates"
              value={stats.emailTemplates}
              icon={<TemplateIcon />}
              color="primary"
              subtitle={`${stats.emailTemplates} active templates`}
              onClick={() => window.location.href = '/email-center'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Emails Sent"
              value={stats.totalEmailsSent}
              icon={<CampaignIcon />}
              color="warning"
              subtitle="Rate-limited & secure"
              onClick={() => window.location.href = '/reports/candidate-updates'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Email Conversations"
              value={stats.emailConversations}
              icon={<MessageIcon />}
              color="success"
              subtitle={`${stats.activeConversations} active this month`}
              onClick={() => window.location.href = '/reports/candidate-updates'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Bulk Email Center"
              value="Launch"
              icon={<EmailIcon />}
              color="secondary"
              subtitle="Send to multiple candidates"
              onClick={() => window.location.href = '/email-center'}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Recent Activities */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            className="glass-card card-hover-effect"
            sx={{
              background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(59, 130, 246, 0.1)',
            }}
          >
            <Box p={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  sx={{ color: '#ffffff', fontWeight: 600 }}
                >
                  Recent Activities
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleOpenModal('recentActivities')}
                  sx={{
                    color: '#60a5fa',
                    '&:hover': {
                      backgroundColor: 'rgba(96, 165, 250, 0.1)',
                    },
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
              <List>
                {recentActivities.map((activity, index) => (
                  <ListItem
                    key={activity.id}
                    divider={index < recentActivities.length - 1}
                    sx={{
                      px: 0,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.05)',
                        borderRadius: '8px',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          color: '#60a5fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'rgba(59, 130, 246, 0.1)',
                        }}
                      >
                        {getActivityIcon(activity.type)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                          {activity.subtitle || 'Recent Activity'}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.85rem',
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 8,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '1.4'
                          }}>
                            {activity.text}
                          </Typography>
                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' }}>
                            {activity.time}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* Upcoming Interviews */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            className="glass-card card-hover-effect"
            sx={{
              background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(245, 158, 11, 0.1)',
            }}
          >
            <Box p={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  sx={{ color: '#ffffff', fontWeight: 600 }}
                >
                  Upcoming Interviews
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleOpenModal('upcomingInterviews')}
                  sx={{
                    color: '#fbbf24',
                    '&:hover': {
                      backgroundColor: 'rgba(251, 191, 36, 0.1)',
                    },
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
              <List>
                {upcomingInterviews.map((interview, index) => (
                  <ListItem
                    key={interview.id}
                    divider={index < upcomingInterviews.length - 1}
                    onClick={() => handleInterviewClick(interview.id)}
                    sx={{
                      px: 0,
                      py: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(245, 158, 11, 0.05)',
                        borderRadius: '8px',
                      },
                      transition: 'all 0.3s ease',
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        sx={{
                          color: '#fbbf24',
                          '&:hover': {
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                          },
                        }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Typography sx={{ color: '#ffffff', fontWeight: 600, mb: 1 }}>
                          {interview.candidate}
                        </Typography>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'block' }}>
                          <Typography
                            component="span"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.85rem',
                              display: 'block',
                              mb: 1,
                            }}
                          >
                            {interview.position} at {interview.company}
                          </Typography>
                          <Chip
                            label={interview.time}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(245, 158, 11, 0.2)',
                              color: '#fbbf24',
                              border: '1px solid rgba(245, 158, 11, 0.3)',
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      }
                        />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Data Modal */}
      <Dialog
        open={!!openModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.95), rgba(51, 65, 85, 0.9))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '16px',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
            color: '#ffffff',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 600,
              textTransform: 'capitalize',
            }}
          >
            {openModal === 'newCandidates' ? 'New Candidates (This Week)' :
             openModal === 'activePositions' ? 'Active Positions' :
             openModal === 'pendingApplications' ? 'Candidates Under Review' :
             openModal === 'hiredCandidates' ? 'Successfully Hired Candidates' :
             openModal === 'recentActivities' ? 'All Recent Activities' :
             openModal === 'upcomingInterviews' ? 'All Upcoming Interviews' :
             openModal === 'candidates' ? 'Total Activated Candidates' :
             openModal === 'interviews' ? 'Upcoming Interviews' :
             openModal?.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, minHeight: '400px', maxHeight: '600px' }}>
          {renderModalContent()}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(59, 130, 246, 0.2)', justifyContent: 'space-between' }}>
          {isPaginatedModal && (
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                onClick={handlePrevPage}
                disabled={!hasPrevPage}
                sx={{
                  color: hasPrevPage ? '#60a5fa' : 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    backgroundColor: hasPrevPage ? 'rgba(96, 165, 250, 0.1)' : 'transparent',
                  },
                }}
              >
                <NavigateBeforeIcon />
              </IconButton>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                Page {currentPage} of {totalPages} ({allModalData.length} total)
              </Typography>
              <IconButton
                onClick={handleNextPage}
                disabled={!hasNextPage}
                sx={{
                  color: hasNextPage ? '#60a5fa' : 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    backgroundColor: hasNextPage ? 'rgba(96, 165, 250, 0.1)' : 'transparent',
                  },
                }}
              >
                <NavigateNextIcon />
              </IconButton>
            </Box>
          )}
          <Button
            onClick={handleCloseModal}
            sx={{
              color: '#60a5fa',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.1)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;